/**
 * Apple IAP website bridge — pricing-only intercept.
 *
 * Explicit capability contract:
 *   - Initial URL must contain BOTH `app=1` AND `native_iap=apple-v1`.
 *   - `window.ReactNativeWebView.postMessage` must be a function at click time.
 * Anything else (browsers, Android app, older builds, missing bridge) → Stripe.
 *
 * This module never logs access tokens, context tokens, appAccountTokens,
 * worker URLs, HMAC values, service keys or customer records. Errors are
 * reduced to bounded machine codes.
 */

export type PlanId = "premium_10d_once" | "premium_30d_once_v2";

export type AppleIapErrorCode =
  | "invalid_response"
  | "network"
  | "unauthorized"
  | "product_mismatch"
  | "expired"
  | "delivery";

export class AppleBridgeError extends Error {
  code: AppleIapErrorCode;
  constructor(code: AppleIapErrorCode) {
    super(code);
    this.code = code;
    this.name = "AppleBridgeError";
  }
}

const APPLE_PRODUCT_BY_PLAN: Record<PlanId, string> = {
  premium_10d_once: "se.alkoholprovet.premium.10days",
  premium_30d_once_v2: "se.alkoholprovet.premium.30days",
};

export function resolveAppleProduct(plan: string): string | null {
  return Object.prototype.hasOwnProperty.call(APPLE_PRODUCT_BY_PLAN, plan)
    ? APPLE_PRODUCT_BY_PLAN[plan as PlanId]
    : null;
}

const LATCH_KEY = "alkoholprovet:native_iap";
const PURCHASE_CONTEXT_URL =
  "https://kqktrqxwxsugoibuerrk.supabase.co/functions/v1/apple-iap-verifier/purchase-context";

const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
// RFC 4122-shaped UUID: version nibble 1..8, variant nibble 8/9/a/b.
// Case-insensitive to match Expo/native contract.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type StorageLike = {
  getItem: (k: string) => string | null;
  setItem: (k: string, v: string) => void;
};

export function computeCapability(
  search: string,
  storage?: StorageLike | null,
): "apple-v1" | null {
  try {
    const params = new URLSearchParams(search);
    if (
      params.get("app") === "1" &&
      params.get("native_iap") === "apple-v1"
    ) {
      try {
        storage?.setItem(LATCH_KEY, "apple-v1");
      } catch {
        /* ignore */
      }
      return "apple-v1";
    }
  } catch {
    /* ignore */
  }
  try {
    if (storage?.getItem(LATCH_KEY) === "apple-v1") return "apple-v1";
  } catch {
    /* ignore */
  }
  return null;
}

let cachedCapability: "apple-v1" | null | undefined;

export function getAppleIapCapability(): "apple-v1" | null {
  if (typeof window === "undefined") return null;
  if (cachedCapability !== undefined) return cachedCapability;
  const w = window as unknown as Record<string, unknown>;
  if (w.__appleIapCapability === "apple-v1" || w.__appleIapCapability === null) {
    cachedCapability = w.__appleIapCapability as "apple-v1" | null;
    return cachedCapability;
  }
  let storage: StorageLike | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  const cap = computeCapability(window.location.search, storage);
  w.__appleIapCapability = cap;
  cachedCapability = cap;
  return cap;
}


type BridgeProbeWindow = {
  ReactNativeWebView?: { postMessage?: unknown };
};

/**
 * Reports whether the native Apple in-app-purchase UI is actually available
 * *right now*: both the explicit `apple-v1` capability latch AND a working
 * `ReactNativeWebView.postMessage` bridge must be present. Pure — never
 * triggers fetch, checkout, or postMessage. Returns `false` under SSR / tests
 * and in normal browsers (no bridge). Reused by `beginPurchase` so purchase
 * routing and Pricing presentation cannot disagree.
 */
export function isNativeAppleAvailable(opts?: {
  capability?: "apple-v1" | null;
  win?: BridgeProbeWindow;
}): boolean {
  const cap = opts?.capability ?? (typeof window !== "undefined" ? getAppleIapCapability() : null);
  if (cap !== "apple-v1") return false;
  const w: BridgeProbeWindow | undefined =
    opts?.win ??
    (typeof window !== "undefined" ? (window as unknown as BridgeProbeWindow) : undefined);
  return typeof w?.ReactNativeWebView?.postMessage === "function";
}


export interface PurchaseContext {
  purchaseContextToken: string;
  appAccountToken: string;
  productId: string;
  expiresAt: string;
}

export function validatePurchaseContext(
  body: unknown,
  expectedProductId: string,
  now: number,
): PurchaseContext {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new AppleBridgeError("invalid_response");
  }
  const keys = Object.keys(body as object).sort().join(",");
  if (keys !== "appAccountToken,expiresAt,productId,purchaseContextToken") {
    throw new AppleBridgeError("invalid_response");
  }
  const b = body as Record<string, unknown>;
  const { purchaseContextToken, appAccountToken, productId, expiresAt } = b;
  if (
    typeof purchaseContextToken !== "string" ||
    !TOKEN_RE.test(purchaseContextToken)
  ) {
    throw new AppleBridgeError("invalid_response");
  }
  if (typeof appAccountToken !== "string" || !UUID_RE.test(appAccountToken)) {
    throw new AppleBridgeError("invalid_response");
  }
  if (typeof productId !== "string") {
    throw new AppleBridgeError("invalid_response");
  }
  if (productId !== expectedProductId) {
    throw new AppleBridgeError("product_mismatch");
  }
  if (typeof expiresAt !== "string") {
    throw new AppleBridgeError("invalid_response");
  }
  const t = Date.parse(expiresAt);
  if (!Number.isFinite(t) || t <= now) {
    throw new AppleBridgeError("expired");
  }
  return { purchaseContextToken, appAccountToken, productId, expiresAt };
}

export async function fetchPurchaseContext(
  productId: string,
  accessToken: string,
  opts?: {
    fetch?: typeof fetch;
    now?: () => number;
    signal?: AbortSignal;
  },
): Promise<PurchaseContext> {
  const f = opts?.fetch ?? fetch;
  const now = opts?.now ?? Date.now;
  let res: Response;
  try {
    res = await f(PURCHASE_CONTEXT_URL, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ productId }),
      signal: opts?.signal,
    });
  } catch {
    throw new AppleBridgeError("network");
  }
  if (res.status === 401 || res.status === 403) {
    throw new AppleBridgeError("unauthorized");
  }
  if (!res.ok) {
    throw new AppleBridgeError("network");
  }
  const ct = (res.headers.get("content-type") ?? "").toLowerCase();
  if (!ct.startsWith("application/json")) {
    throw new AppleBridgeError("invalid_response");
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new AppleBridgeError("invalid_response");
  }
  return validatePurchaseContext(body, productId, now());
}

type BridgeWindow = {
  ReactNativeWebView?: { postMessage: (s: string) => void };
};

export function postAppleIapMessage(
  payload: {
    productId: string;
    appAccountToken: string;
    purchaseContextToken: string;
  },
  win?: BridgeWindow,
): void {
  const w: BridgeWindow | undefined =
    win ??
    (typeof window !== "undefined"
      ? (window as unknown as BridgeWindow)
      : undefined);
  const bridge = w?.ReactNativeWebView;
  if (!bridge || typeof bridge.postMessage !== "function") {
    throw new AppleBridgeError("delivery");
  }
  const message = JSON.stringify({
    type: "OPEN_APPLE_IAP",
    productId: payload.productId,
    appAccountToken: payload.appAccountToken,
    purchaseContextToken: payload.purchaseContextToken,
  });
  try {
    bridge.postMessage(message);
  } catch {
    throw new AppleBridgeError("delivery");
  }
}

let inFlight = false;

export type BeginPurchaseResult =
  | "stripe"
  | "needs_login"
  | "apple"
  | "error"
  | "busy";

export interface BeginPurchaseArgs {
  plan: string;
  accessToken: string | null | undefined;
  onError?: (code: AppleIapErrorCode) => void;
  /** Overrides for tests. */
  capability?: "apple-v1" | null;
  win?: BridgeWindow;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

export async function beginPurchase(
  args: BeginPurchaseArgs,
): Promise<BeginPurchaseResult> {
  const cap = args.capability ?? getAppleIapCapability();
  const product = resolveAppleProduct(args.plan);
  if (!product) return "stripe";
  const w: BridgeWindow | undefined =
    args.win ??
    (typeof window !== "undefined"
      ? (window as unknown as BridgeWindow)
      : undefined);
  if (!isNativeAppleAvailable({ capability: cap, win: w })) return "stripe";
  if (!args.accessToken) return "needs_login";

  if (inFlight) return "busy";
  inFlight = true;
  try {
    let ctx: PurchaseContext;
    try {
      ctx = await fetchPurchaseContext(product, args.accessToken, {
        fetch: args.fetchImpl,
        now: args.now,
      });
    } catch (e) {
      const code =
        e instanceof AppleBridgeError ? e.code : ("network" as AppleIapErrorCode);
      args.onError?.(code);
      return "error";
    }
    try {
      postAppleIapMessage(
        {
          productId: product,
          appAccountToken: ctx.appAccountToken,
          purchaseContextToken: ctx.purchaseContextToken,
        },
        w,
      );
    } catch (e) {
      const code =
        e instanceof AppleBridgeError ? e.code : ("delivery" as AppleIapErrorCode);
      args.onError?.(code);
      return "error";
    }
    return "apple";
  } finally {
    inFlight = false;
  }
}

/** Test-only helpers. Not part of the runtime contract. */
export const __test = {
  resetInFlight: () => {
    inFlight = false;
  },
  resetCapabilityCache: () => {
    cachedCapability = undefined;
  },
  isInFlight: () => inFlight,
  PURCHASE_CONTEXT_URL,
};
